export type TeenParentalControlsUpdate = Partial<{
  dailySpendingLimitCents: number;
  perOrderSpendingLimitCents: number;
  allowTeenPersonalCards: boolean;
  requirePreApproval: boolean;
  allowedStoreHubs?: string[];
  suspended?: boolean;
}>;

import { z } from 'zod';

import { API_ROUTES, API_PATHS } from '../../../lib/apiRoutes';
import { apiRequest } from '../../../lib/api';
import { buildStickyIdempotencyKey } from '../../../lib/idempotency';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import type { User, UserStatsSummary, UserUpdatePayload, UsersStatsListResponse } from '../types/users.types';

const objectIdSchema = z.string().trim().regex(/^[a-f\d]{24}$/i, 'Invalid user id');

const userStatsSchema = z.object({
  userId: z.string().trim().min(1),
  orderCount: z.number().int().nonnegative().default(0),
  totalSpend: z.number().nonnegative().default(0),
  lastOrderAt: z.string().nullable().optional()
}).passthrough();

const usersListEnvelopeSchema = z.object({
  success: z.boolean().optional(),
  data: z.array(z.custom<User>()),
  count: z.number().int().nonnegative().optional(),
  nextCursor: z.string().nullable().default(null),
  traceId: z.string().optional()
}).passthrough();

export interface UsersListQuery {
  /** Server searches email and username only. */
  q?: string;
  /** Lowercase server role, e.g. `affiliate`. */
  role?: string;
  cursor?: string | null;
  limit?: number;
}

export interface UsersListPage {
  users: User[];
  nextCursor: string | null;
}

const userEnvelopeSchema = z.object({
  success: z.boolean().optional(),
  data: z.record(z.string(), z.unknown()),
  traceId: z.string().optional()
}).passthrough();

const usersStatsEnvelopeSchema = z.object({
  success: z.boolean().optional(),
  data: z.object({
    stats: z.array(userStatsSchema).default([])
  }),
  traceId: z.string().optional()
}).passthrough();

const singleUserStatsEnvelopeSchema = z.object({
  success: z.boolean().optional(),
  data: z.object({
    stats: userStatsSchema
  }),
  traceId: z.string().optional()
}).passthrough();

export const usersApi = {
  list: async (query: UsersListQuery = {}): Promise<UsersListPage> => {
    const response = await apiRequest<unknown>(API_ROUTES.v1.users, {
      query: {
        q: query.q?.trim() || undefined,
        role: query.role || undefined,
        cursor: query.cursor || undefined,
        limit: query.limit
      }
    });
    const envelope = validateWithSchema(usersListEnvelopeSchema, response, API_ROUTES.v1.users);
    return { users: envelope.data, nextCursor: envelope.nextCursor };
  },
  listStats: () =>
    apiRequest<unknown>(`${API_ROUTES.v1.users}/stats`)
      .then((response) => validateWithSchema(usersStatsEnvelopeSchema, response, `${API_ROUTES.v1.users}/stats`)) as Promise<{ data: UsersStatsListResponse }>,
  getStats: (userId: string) =>
    apiRequest<unknown>(`${API_ROUTES.v1.users}/${objectIdSchema.parse(userId)}/stats`)
      .then((response) => validateWithSchema(singleUserStatsEnvelopeSchema, response, `${API_ROUTES.v1.users}/:id/stats`)) as Promise<{ data: { stats: UserStatsSummary } }>,
  update: (id: string, payload: UserUpdatePayload) => {
    const bodyPayload: UserUpdatePayload = { ...payload };
    if (typeof bodyPayload.role === 'string') {
      bodyPayload.role = bodyPayload.role.toLowerCase();
    }
    return apiRequest<unknown>(`${API_ROUTES.v1.users}/${objectIdSchema.parse(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(bodyPayload)
    }).then((response) => validateWithSchema(userEnvelopeSchema, response, `${API_ROUTES.v1.users}/:id`)) as Promise<{ data: User }>;
  },
  /** Privacy deletion (PII scrub + tombstone); orders and ledger history are retained. */
  remove: (id: string, idempotencyKey: string) =>
    apiRequest<unknown>(`${API_ROUTES.v1.users}/${objectIdSchema.parse(id)}`, {
      method: 'DELETE',
      idempotencyKey,
      requireIdempotencyKey: true
    }),
  getMyTeens() {
    return apiRequest<{ success: boolean; data: { linked: User[]; pending: User[] } }>(API_PATHS.users.myTeens);
  },
  approveTeen(payload: {
    teenId: string;
    dailySpendingLimitCents: number;
    perOrderSpendingLimitCents: number;
    allowTeenPersonalCards: boolean;
    requirePreApproval: boolean;
    allowedStoreHubs?: string[];
    lockedStoreHubs?: string[];
  }) {
    return apiRequest<{ success: boolean; data: User }>(API_PATHS.users.approveTeen, {
      method: 'POST',
      body: JSON.stringify(payload),
      idempotencyKey: buildStickyIdempotencyKey('teen-approve-link', [payload.teenId])
    });
  },
  updateTeenControls(teenId: string, payload: TeenParentalControlsUpdate | Partial<{
    dailySpendingLimitCents: number;
    perOrderSpendingLimitCents: number;
    allowTeenPersonalCards: boolean;
    requirePreApproval: boolean;
    allowedStoreHubs?: string[];
    lockedStoreHubs?: string[];
    suspended?: boolean;
  }>) {
    return apiRequest<{ success: boolean; data: User }>(API_PATHS.users.updateTeenControls(teenId), {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  },
  registerTeen(payload: {
    username: string;
    email: string;
    password: string;
    dateOfBirth: string;
    parentEmail: string;
    acceptedTermsPrivacy: boolean;
  }) {
    return apiRequest<{ success: boolean; data: { teenId: string; user?: User } }>(`${API_ROUTES.v1.users}/teen/register`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }
};
